const sum = (items, prop) => {
    return items.reduce((a, b) => {
        return a + b[prop];
    }, 0);
  };

// ESPN drops `rosterForCurrentScoringPeriod` for completed seasons, keeping only
// the side's final total. Fall back to that so past seasons show real scores
// instead of NaN. Returns 0 when neither is available (e.g. preseason).
const finalScoreFallback = team => team?.totalPointsLive ?? team?.totalPoints ?? 0;

export const totalScore = team => {
    if (team && team.rosterForCurrentScoringPeriod) {
      const starters = team.rosterForCurrentScoringPeriod.entries
                          .filter(entry => entry.lineupSlotId !== 20 && entry.lineupSlotId !== 21)
                          .map(player => player.playerPoolEntry);
      return sum(starters, "appliedStatTotal");
    } else {
      return finalScoreFallback(team);
    }
  };

  export const projectedPoints = (team) => {
    const BENCH_SLOT_IDS = [20, 21];

    const getPlayerPoints = (player) => {
      const stats = player.playerPoolEntry?.player.stats;
      if (!stats || stats.length === 0) return 0;
  
      const gameCompleted = hasPlayedGame(player);
  
      if (gameCompleted) {
        const completedStats = stats.find(stat => stat.variance && Object.keys(stat.variance).length === 0);
        return completedStats?.appliedTotal || 0;
      } else {
        // For games not completed, use the higher appliedTotal
        return Math.max(...stats.map(stat => stat?.appliedTotal || 0));
      }
    };
  
    // No roster means a completed season: the final score is the projection.
    if (!team?.rosterForCurrentScoringPeriod?.entries) {
      return finalScoreFallback(team);
    }

    // ESPN's own live projection, matching the "Proj Total" on its scoreboard.
    // It blends points scored so far with each player's projection over the
    // game time remaining, so it moves during live games. The per-player
    // estimate below is only a fallback for responses without it.
    if (typeof team.totalProjectedPointsLive === 'number') {
      return team.totalProjectedPointsLive;
    }

    return team.rosterForCurrentScoringPeriod.entries
      .filter(player => !BENCH_SLOT_IDS.includes(player.lineupSlotId))
      .reduce((sum, player) => sum + getPlayerPoints(player), 0);
  };
  
// A game counts as live from kickoff until ESPN marks its stats official. The
// cap covers ESPN being slow to flip that flag (games run ~3.5h).
const MAX_GAME_LENGTH_MS = 6 * 60 * 60 * 1000;

export const isAnyGameLive = (games, now) =>
  games.some(game => game.date <= now && !game.statsOfficial && now - game.date < MAX_GAME_LENGTH_MS);

// How long until the next kickoff, capped so a long gap (or an empty
// schedule) still gets rechecked periodically.
export const msUntilNextKickoff = (games, now, cap) => {
  const upcoming = games.map(game => game.date - now).filter(ms => ms > 0);
  return Math.min(cap, ...upcoming);
};

  export const playerIsActiveInRoster = player => {
    return player.lineupSlotId !== 20 && player.lineupSlotId !== 21;
  }

const hasPlayedGame = (entry) => {
  const stats = entry.playerPoolEntry?.player.stats;
return stats && stats.some(stat => stat?.variance && Object.keys(stat.variance).length === 0);
};

// Each NFL team's game in one scoring period, keyed by proTeamId.
export const proGamesByTeam = (games, scoringPeriodId) => {
  const byTeam = {};
  games
    .filter(game => game.scoringPeriodId === scoringPeriodId)
    .forEach(game => {
      byTeam[game.homeProTeamId] = game;
      byTeam[game.awayProTeamId] = game;
    });
  return byTeam;
};

// 'done' | 'playing' | 'remaining' for one rostered player, from the state of
// their NFL game. A player on bye has nothing left to score, so counts as done.
const playerGameState = (entry, gamesByTeam, now) => {
  const game = gamesByTeam[entry.playerPoolEntry?.player?.proTeamId];
  if (!game || game.statsOfficial || now - game.date >= MAX_GAME_LENGTH_MS) return 'done';
  return game.date <= now ? 'playing' : 'remaining';
};

// Starter counts by game state, matching ESPN's "Currently Playing" / "Yet to
// Play". Null until the NFL schedule has loaded, so callers can hide the line
// rather than briefly show every player as done.
export const getTeamStats = (team, gamesByTeam, now) => {
  if (Object.keys(gamesByTeam).length === 0) return null;
  const starters = (team?.rosterForCurrentScoringPeriod?.entries ?? [])
    .filter(playerIsActiveInRoster)
    .filter(entry => entry.playerPoolEntry);
  const states = starters.map(entry => playerGameState(entry, gamesByTeam, now));
  const count = state => states.filter(s => s === state).length;
  return {
    playersDone: count('done'),
    playersPlaying: count('playing'),
    playersRemaining: count('remaining'),
  };
};

// The commissioner renames teams to mark their status: "e-" (formerly
// "ELIM") once eliminated, "zombie-" for a team that is out but still plays.
export const isEliminatedTeam = name => /^(e-|elim)/i.test(name?.trim() ?? '');
export const isZombieTeam = name => /^zombie/i.test(name?.trim() ?? '');

// The `count` lowest-scoring teams still alive, lowest first: the teams in
// line for elimination. Eliminated teams are out, and a zombie isn't counted
// here (it goes out separately if it finishes below the median).
export const getDangerZone = (teams, count, scoreKey) =>
  teams
    .filter(team => !isEliminatedTeam(team.teamName) && !isZombieTeam(team.teamName))
    .sort((a, b) => a[scoreKey] - b[scoreKey])
    .slice(0, count);
